import { Injectable } from '@angular/core';
import { LoggerEvent, LoggerProvider } from '../logger/logger';

export interface IDuitNowShareImage {
  /** `data:image/png;base64,...` of the bare DuitNow QR returned by the API. */
  qrDataUrl: string;
  /** Full name of the wallet holder the payer will be paying. */
  recipientName: string;
  /** Scheme identification shown under the QR, e.g. 'Malaysia National QR'. */
  schemeLabel: string;
  /** Instruction line shown at the foot of the card. */
  instructions: string;
}

interface IFittedText {
  lines: string[];
  fontSize: number;
  lineHeight: number;
}

// Geometry of the exported card, in canvas pixels.
const LAYOUT = {
  width: 1080,          // 9:16 so the export drops cleanly into any chat or story surface
  height: 1920,
  contentMaxWidth: 800, // wrap width shared by the recipient name and the instructions

  logoHeight: 155,
  dividerWidth: 3,
  dividerHeight: 150,
  lockupGap: 74,
  logoGap: 165,

  nameFontSizes: [66, 58, 50],
  nameLineHeightRatio: 1.27,
  nameMaxLines: 2,
  nameGap: 140,

  panelWidth: 610,
  panelPadding: 38,
  panelGap: 160,
  qrBoxPadding: 28,
  labelFontSize: 32,
  labelGap: 40,

  bodyFontSize: 38,
  bodyLineHeight: 55
};

const WHITE = '#FFFFFF';

/**
 * Composes the image a customer shares from the MY DuitNow QR Receive screen.
 *
 * Ria is an indirect DuitNow participant, so a third party scanning the code only ever sees the
 * participant merchant name — the exported image is the one surface where the payer can be shown
 * who they are actually paying. This provider turns the bare QR PNG into a branded card carrying
 * the Ria + DuitNow lockup, the recipient name, the scheme label and the scan instructions.
 *
 * Everything is drawn straight onto a canvas rather than rasterised from the DOM: the export has to
 * be identical across iOS and Android WebViews, and it must not depend on the live layout of the
 * screen behind it.
 */
@Injectable()
export class DuitNowQrShareProvider {

  /**
   * Renders the branded card and returns it as a PNG data URL.
   * Rejects if the canvas is unavailable or an asset fails to load — callers are expected to fall
   * back to sharing the bare QR rather than blocking the share.
   */
  async buildShareImage(options: IDuitNowShareImage): Promise<string> {
    if (!options || !options.qrDataUrl)
      throw new Error('DuitNow share image requires a QR data URL');

    await this.waitForFonts();

    const [qrImage, riaLogo, duitNowLogo] = await Promise.all([
      this.loadImage(options.qrDataUrl),
      this.loadImage('assets/icons/ria-name-white.svg'),
      this.loadImage('assets/icons/duitnow_share_white.png')
    ]);

    const canvas = document.createElement('canvas');
    canvas.width = LAYOUT.width;
    canvas.height = LAYOUT.height;

    const ctx = canvas.getContext('2d');
    if (!ctx)
      throw new Error('2d canvas context unavailable');

    this.paintBackground(ctx);

    // Measure everything first so the whole card can be centred vertically regardless of how many
    // lines the name and the instructions wrap onto.
    const name = this.fitName(ctx, options.recipientName);
    const bodyLines = this.wrapText(ctx, options.instructions, this.font('400', LAYOUT.bodyFontSize), LAYOUT.contentMaxWidth, 4);
    const panelHeight = this.panelHeight();

    const contentHeight =
      LAYOUT.logoHeight + LAYOUT.logoGap +
      (name.lines.length * name.lineHeight) + LAYOUT.nameGap +
      panelHeight + LAYOUT.panelGap +
      (bodyLines.length * LAYOUT.bodyLineHeight);

    let y = Math.max(120, (LAYOUT.height - contentHeight) / 2);

    y = this.paintLockup(ctx, riaLogo, duitNowLogo, y);
    y = this.paintNameLines(ctx, name, y);
    y = this.paintPanel(ctx, qrImage, options.schemeLabel, y, panelHeight);
    this.paintBodyLines(ctx, bodyLines, y);

    return canvas.toDataURL('image/png');
  }

  // ---------------------------------------------------------------------------------------------
  // Painting
  // ---------------------------------------------------------------------------------------------

  private paintBackground(ctx: CanvasRenderingContext2D) {
    const base = ctx.createLinearGradient(0, 0, 0, LAYOUT.height);
    base.addColorStop(0, '#3A0E1D');
    base.addColorStop(0.42, '#1E070D');
    base.addColorStop(1, '#140306');
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, LAYOUT.width, LAYOUT.height);

    // Crimson glow behind the lockup. Squashed vertically so it reads as an ellipse — canvas only
    // has circular radial gradients.
    const radius = LAYOUT.width * 0.72;

    ctx.save();
    ctx.translate(LAYOUT.width / 2, LAYOUT.height * 0.16);
    ctx.scale(1, 0.62);
    const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, radius);
    glow.addColorStop(0, 'rgba(241, 46, 104, 0.22)');
    glow.addColorStop(1, 'rgba(241, 46, 104, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(-radius, -radius, radius * 2, radius * 2);
    ctx.restore();
  }

  /** Draws `ria | DuitNow` centred at `y` and returns the y the next block starts at. */
  private paintLockup(ctx: CanvasRenderingContext2D, riaLogo: HTMLImageElement, duitNowLogo: HTMLImageElement, y: number): number {
    const centreY = y + (LAYOUT.logoHeight / 2);
    const halfDivider = LAYOUT.dividerWidth / 2;
    const duitNowWidth = LAYOUT.logoHeight * (64 / 57);
    const riaWidth = duitNowWidth * 0.72;
    const riaHeight = riaWidth / (43 / 28);

    // The divider sits on the centre line and each mark hangs off it. Centring the lockup's bounding
    // box instead would push the divider left, because the DuitNow mark is the wider of the two.
    const centreX = LAYOUT.width / 2;

    ctx.drawImage(riaLogo, centreX - halfDivider - LAYOUT.lockupGap - riaWidth, centreY - (riaHeight / 2), riaWidth, riaHeight);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.fillRect(centreX - halfDivider, centreY - (LAYOUT.dividerHeight / 2), LAYOUT.dividerWidth, LAYOUT.dividerHeight);

    ctx.drawImage(duitNowLogo, centreX + halfDivider + LAYOUT.lockupGap, centreY - (LAYOUT.logoHeight / 2), duitNowWidth, LAYOUT.logoHeight);

    return y + LAYOUT.logoHeight + LAYOUT.logoGap;
  }

  private paintNameLines(ctx: CanvasRenderingContext2D, name: IFittedText, y: number): number {
    ctx.font = this.font('700', name.fontSize);
    ctx.fillStyle = WHITE;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    let lineTop = y;
    for (const line of name.lines) {
      // Baseline sits roughly three quarters down the line box, which keeps ascenders and
      // descenders inside the measured block height.
      ctx.fillText(line, LAYOUT.width / 2, lineTop + (name.lineHeight * 0.75));
      lineTop += name.lineHeight;
    }

    return lineTop + LAYOUT.nameGap;
  }

  /**
   * Draws the pink card holding the white QR box and the scheme label, mirroring the
   * `.duitnow-qr-border` / `.duitnow-qr-inner` / `.duitnow-label` nesting used on screen.
   */
  private paintPanel(ctx: CanvasRenderingContext2D, qr: HTMLImageElement, schemeLabel: string, y: number, panelHeight: number): number {
    const panelX = (LAYOUT.width - LAYOUT.panelWidth) / 2;
    const boxSize = LAYOUT.panelWidth - (LAYOUT.panelPadding * 2);
    const boxX = panelX + LAYOUT.panelPadding;
    const boxY = y + LAYOUT.panelPadding;
    const qrSize = boxSize - (LAYOUT.qrBoxPadding * 2);

    const panelFill = ctx.createLinearGradient(0, y, 0, y + panelHeight);
    panelFill.addColorStop(0, '#F12E68');
    panelFill.addColorStop(1, '#DD1150');
    ctx.fillStyle = panelFill;
    this.roundRect(ctx, panelX, y, LAYOUT.panelWidth, panelHeight, 60);
    ctx.fill();

    ctx.fillStyle = WHITE;
    this.roundRect(ctx, boxX, boxY, boxSize, boxSize, 36);
    ctx.fill();

    ctx.drawImage(qr, boxX + LAYOUT.qrBoxPadding, boxY + LAYOUT.qrBoxPadding, qrSize, qrSize);

    if (schemeLabel) {
      // Translations of this label run much longer than the English 'Malaysia National QR'
      // (Russian is ~50% wider), so it is shrunk to fit rather than allowed to bleed past the panel.
      const label = this.fitLabel(ctx, schemeLabel.toUpperCase(), boxSize);
      ctx.font = this.font('700', label.fontSize);
      ctx.fillStyle = WHITE;
      ctx.textAlign = 'center';
      ctx.fillText(label.text, LAYOUT.width / 2, boxY + boxSize + LAYOUT.labelGap + LAYOUT.labelFontSize);
    }

    return y + panelHeight + LAYOUT.panelGap;
  }

  private paintBodyLines(ctx: CanvasRenderingContext2D, lines: string[], y: number) {
    ctx.font = this.font('400', LAYOUT.bodyFontSize);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.65)';
    ctx.textAlign = 'center';

    let lineTop = y;
    for (const line of lines) {
      ctx.fillText(line, LAYOUT.width / 2, lineTop + (LAYOUT.bodyLineHeight * 0.75));
      lineTop += LAYOUT.bodyLineHeight;
    }
  }

  // ---------------------------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------------------------

  /**
   * Picks the largest name size that still fits inside `nameMaxLines`. Only if even the smallest
   * size overflows does the name get ellipsised — truncating the recipient's name would defeat the
   * point of the card.
   */
  private fitName(ctx: CanvasRenderingContext2D, recipientName: string): IFittedText {
    for (const fontSize of LAYOUT.nameFontSizes) {
      const lines = this.wrapText(ctx, recipientName, this.font('700', fontSize), LAYOUT.contentMaxWidth, Number.MAX_SAFE_INTEGER);
      if (lines.length <= LAYOUT.nameMaxLines)
        return { lines, fontSize, lineHeight: Math.round(fontSize * LAYOUT.nameLineHeightRatio) };
    }

    const smallest = LAYOUT.nameFontSizes[LAYOUT.nameFontSizes.length - 1];
    return {
      lines: this.wrapText(ctx, recipientName, this.font('700', smallest), LAYOUT.contentMaxWidth, LAYOUT.nameMaxLines),
      fontSize: smallest,
      lineHeight: Math.round(smallest * LAYOUT.nameLineHeightRatio)
    };
  }

  /** Shrinks the scheme label until it fits `maxWidth`, ellipsising only as a last resort. */
  private fitLabel(ctx: CanvasRenderingContext2D, label: string, maxWidth: number): { text: string; fontSize: number } {
    let fontSize = LAYOUT.labelFontSize;
    ctx.font = this.font('700', fontSize);

    while (fontSize > 22 && ctx.measureText(label).width > maxWidth) {
      fontSize -= 1;
      ctx.font = this.font('700', fontSize);
    }

    const text = ctx.measureText(label).width > maxWidth ? this.ellipsise(ctx, label, maxWidth) : label;
    return { text, fontSize };
  }

  private panelHeight(): number {
    const boxSize = LAYOUT.panelWidth - (LAYOUT.panelPadding * 2);
    return LAYOUT.panelPadding + boxSize + LAYOUT.labelGap + LAYOUT.labelFontSize + 34;
  }
  private font(weight: string, size: number): string {
    return `${weight} ${size}px Inter, sans-serif`;
  }

  /**
   * Greedy word wrap. Anything past `maxLines` is dropped and the last line is ellipsised, so an
   * unusually long name can never push the QR off the card.
   */
  private wrapText(ctx: CanvasRenderingContext2D, text: string, font: string, maxWidth: number, maxLines: number): string[] {
    const value = (text || '').trim();
    if (!value)
      return [];

    ctx.font = font;

    const lines: string[] = [];
    let current = '';

    for (const token of this.tokenise(ctx, value, maxWidth)) {
      const candidate = current ? `${current} ${token}` : token;
      if (current && ctx.measureText(candidate).width > maxWidth) {
        lines.push(current);
        current = token;
      } else {
        current = candidate;
      }
    }
    if (current)
      lines.push(current);

    if (lines.length <= maxLines)
      return lines;

    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = this.ellipsise(ctx, kept[maxLines - 1], maxWidth);
    return kept;
  }

  /**
   * The words to lay out, with any token wider than the whole line box broken into chunks that fit.
   * The recipient name is user data and can arrive as one unbroken string; without this it is
   * emitted as a single over-wide line and bleeds off both edges of the card.
   *
   * Chunks are safe to re-join with a space in the caller: every chunk but the last is maximal, so
   * a chunk plus anything else always overflows and wraps.
   */
  private tokenise(ctx: CanvasRenderingContext2D, value: string, maxWidth: number): string[] {
    const tokens: string[] = [];

    for (const word of value.split(/\s+/)) {
      if (ctx.measureText(word).width <= maxWidth)
        tokens.push(word);
      else
        tokens.push(...this.breakWord(ctx, word, maxWidth));
    }

    return tokens;
  }

  /** Splits a single over-wide token into chunks that each fit `maxWidth`. */
  private breakWord(ctx: CanvasRenderingContext2D, word: string, maxWidth: number): string[] {
    const chunks: string[] = [];
    let chunk = '';
    let i = 0;

    while (i < word.length) {
      // Step by code point, so a surrogate pair is never split into two broken halves.
      const code = word.charCodeAt(i);
      const size = (code >= 0xD800 && code <= 0xDBFF && i + 1 < word.length) ? 2 : 1;
      const char = word.substr(i, size);
      i += size;

      if (chunk && ctx.measureText(chunk + char).width > maxWidth) {
        chunks.push(chunk);
        chunk = char;
      } else {
        chunk += char;
      }
    }

    if (chunk)
      chunks.push(chunk);

    return chunks;
  }

  private ellipsise(ctx: CanvasRenderingContext2D, line: string, maxWidth: number): string {
    let truncated = line;
    while (truncated.length > 1 && ctx.measureText(`${truncated}…`).width > maxWidth) {
      truncated = truncated.slice(0, -1);
    }
    return `${truncated}…`;
  }

  /** `ctx.roundRect` is not available in the WebView versions this app still supports. */
  private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
    const r = Math.min(radius, width / 2, height / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + r);
    ctx.lineTo(x + width, y + height - r);
    ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    ctx.lineTo(x + r, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  private async loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error(`Failed to load ${src} for DuitNow share card`));
      image.src = src;
    });
  }

  /**
   * Canvas silently falls back to a system font if the app webfont has not finished loading, which
   * would ship a differently-typeset card. Best effort only — `document.fonts` is not everywhere.
   */
  private async waitForFonts(): Promise<void> {
    try {
      const fonts = (document as any).fonts;
      if (fonts && fonts.ready)
        await fonts.ready;
    } catch (ex) {
      LoggerProvider.warning(this, LoggerEvent.Wallet, 'Font readiness check unavailable for DuitNow share card');
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/duitnow-qr-share/duitnow-qr-share.ts